import axios from 'axios';
import { useQuery } from 'react-query';
import request from '../../../../helpers/request';

export const queryOptions = {
  notifyOnChangeProps: 'tracked',
  staleTime: Infinity,
};

export const createQueryData = ({ endPoint, options = {}, custom, key }) => {
  const {
    errorMessage,
    createParams = mapEndPointToUrl,
    arrayFormat,
  } = options;

  const createKey = key
    ? () => key
    : (params = {}) => [endPoint, ...Object.values(params)];

  const fn = (params = {}, source) => {
    const { url, params: _params, data } = createParams({ endPoint, params });

    return !custom
      ? request.get(
          url,
          _params,
          errorMessage,
          'Error',
          undefined,
          arrayFormat,
          {
            cancelToken: source.token,
            data,
          },
          !errorMessage
        )
      : custom(params);
  };

  const cancelableFn = (params) =>
    withCancelToken((source) => fn(params, source));

  const useFetch = ({ params = {}, options = {} } = {}) => {
    return useQuery(createKey(params), () => cancelableFn(params), {
      ...queryOptions,
      ...options,
    });
  };

  return { createKey, fn: cancelableFn, useQuery: useFetch };
};

const returnTypes = { status: 'status', result: 'result' };
export const createQueriesState = (
  queries,
  returnType = returnTypes.status
) => {
  let isLoading = false;
  let refetch = () => {};
  const refetchStack = [];
  let status;
  const result = [];

  queries.forEach((query) => {
    const { data, isFetching, refetch } = query;

    if (!isLoading && isFetching) {
      isLoading = true;
    }

    if (data === 'Error') {
      refetchStack.push(refetch);
      status = 'Error';
    } else if (data !== undefined) {
      result.push(data);
    }
  });

  if (result.length === queries.length) {
    status = 'Success';
  }

  if (refetchStack.length) {
    refetch = () => Promise.allSettled(refetchStack.map((fn) => fn()));
  }

  const outputMap = {
    [returnTypes.status]: status,
    [returnTypes.result]: result,
  };

  return {
    data: !status || status === 'Error' ? status : outputMap[returnType],
    isFetching: isLoading,
    refetch,
  };
};

createQueriesState.returnTypes = returnTypes;

function mapEndPointToUrl({ endPoint, params }) {
  return { url: endPoint, params };
}

function withCancelToken(fn) {
  const source = axios.CancelToken.source();
  const promise = fn(source);

  promise.cancel = () => {
    source.cancel();
  };

  return promise;
}
